import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Globe, Lock, DollarSign, Swords, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type WBRecord = {
  country: string;
  countryCode: string;
  indicator: string;
  indicatorCode: string;
  value: number;
  year: string;
};

const indicatorIcons: Record<string, any> = {
  "NY.GDP.MKTP.CD": DollarSign,
  "FP.CPI.TOTL.ZG": Globe,
  "MS.MIL.XPND.GD.ZS": Swords,
  "SP.POP.TOTL": Users,
};

const formatValue = (code: string, val: number) => {
  if (code === "NY.GDP.MKTP.CD") {
    if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
    if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
    return `$${(val / 1e6).toFixed(0)}M`;
  }
  if (code === "SP.POP.TOTL") {
    if (val >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
    if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    return val.toLocaleString();
  }
  return `${val.toFixed(2)}%`;
};

const WorldBankData = () => {
  const { isPremium } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["world-bank"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-world-bank");
      if (error) throw error;
      return data as { data: WBRecord[]; fetchedAt: string };
    },
    staleTime: 3600000,
    retry: 2,
  });

  const records = data?.data || [];
  const indicators = [...new Set(records.map((r) => r.indicatorCode))];

  const groupByIndicator = (code: string) =>
    records.filter((r) => r.indicatorCode === code).sort((a, b) => b.value - a.value);

  return (
    <section id="world-bank" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            World Economic Indicators
          </h2>
          <Badge variant="outline" className="text-[9px] font-mono text-muted-foreground">World Bank — Most Recent Available</Badge>
        </div>

        {isLoading && !data ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading economic data...</div>
        ) : (
          <Tabs defaultValue={indicators[0] || "NY.GDP.MKTP.CD"} className="w-full">
            <TabsList className="mb-4 flex-wrap">
              {indicators.map((code) => {
                const name = records.find((r) => r.indicatorCode === code)?.indicator || code;
                return <TabsTrigger key={code} value={code} className="text-[10px]">{name.split("(")[0].trim()}</TabsTrigger>;
              })}
            </TabsList>

            {indicators.map((code) => {
              const rows = groupByIndicator(code);
              const Icon = indicatorIcons[code] || Globe;
              return (
                <TabsContent key={code} value={code}>
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <div className="grid grid-cols-[1fr_100px_60px] gap-2 px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                      <span>Country</span>
                      <span className="text-right">Value</span>
                      <span className="text-right">Year</span>
                    </div>
                    <div className="divide-y divide-border/50 max-h-[350px] overflow-y-auto">
                      {rows.map((r, i) => (
                        <div
                          key={r.countryCode}
                          className={`grid grid-cols-[1fr_100px_60px] items-center gap-2 px-3 py-2.5 transition-colors hover:bg-accent/50 ${!isPremium && i >= 5 ? "blur-sm select-none" : ""}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-xs text-foreground">{r.country}</span>
                            <span className="text-[9px] text-muted-foreground font-mono">{r.countryCode}</span>
                          </div>
                          <span className="text-right text-xs font-mono font-semibold text-foreground">
                            {formatValue(code, r.value)}
                          </span>
                          <span className="text-right text-[10px] font-mono text-muted-foreground">{r.year}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        {!isPremium && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <Lock className="mr-1 inline h-3 w-3" />
              Free users see top 5 countries. <span className="text-primary font-semibold cursor-pointer">Upgrade to Premium</span> for full data.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default WorldBankData;
